import { useQuery } from '@tanstack/react-query';
import { CalendarCheck, Gavel, MailCheck, Sigma } from 'lucide-react';

import { api } from '@/lib/api';
import type { EtapasTempo, RelatorioTempos } from '@/features/relatorios/tipos';
import { formatarDuracao, formatarInteiro } from '@/features/relatorios/formato';
import { GraficoRosca } from '@/components/graficos/rosca';
import {
  Tabela,
  TabelaCabecalho,
  TabelaCelula,
  TabelaCorpo,
  TabelaLinha,
  TabelaTitulo,
  TabelaVazia,
} from '@/components/ui/table';
import { CartaoGrafico, Indicador } from './cartoes';
import { EsqueletoBloco } from './esqueleto-bloco';
import { ErroBloco } from './erro-bloco';

const ROTULOS_ORIGEM: Record<'ESUS' | 'MANUAL', string> = {
  ESUS: 'Integração eSUS',
  MANUAL: 'Digitado pela Regulação',
};

/** Mediana em destaque e média abaixo — ver a nota no cabeçalho do bloco. */
function CelulaEtapa({
  etapa,
  rotulo,
}: {
  etapa: { media: number | null; mediana: number | null };
  rotulo: string;
}) {
  return (
    <TabelaCelula rotulo={rotulo} className="text-right">
      <div className="tabular-nums">{formatarDuracao(etapa.mediana)}</div>
      <div className="text-silenciado-texto text-xs tabular-nums">
        média {formatarDuracao(etapa.media)}
      </div>
    </TabelaCelula>
  );
}

function LinhasEtapas({
  linhas,
  rotuloColuna,
  vazio,
}: {
  linhas: (EtapasTempo & { chave: string; nome: string })[];
  rotuloColuna: string;
  vazio: string;
}) {
  return (
    <Tabela>
      <TabelaCabecalho>
        <TabelaLinha>
          <TabelaTitulo>{rotuloColuna}</TabelaTitulo>
          <TabelaTitulo className="text-right">Solicitações</TabelaTitulo>
          <TabelaTitulo className="text-right">Até a leitura</TabelaTitulo>
          <TabelaTitulo className="text-right">Até a decisão</TabelaTitulo>
          <TabelaTitulo className="text-right">Até o agendamento</TabelaTitulo>
        </TabelaLinha>
      </TabelaCabecalho>
      <TabelaCorpo>
        {linhas.length === 0 && <TabelaVazia colunas={5}>{vazio}</TabelaVazia>}

        {linhas.map((linha) => (
          <TabelaLinha key={linha.chave}>
            <TabelaCelula rotulo={rotuloColuna} className="font-medium">
              {linha.nome}
            </TabelaCelula>
            <TabelaCelula rotulo="Solicitações" className="text-right tabular-nums">
              {formatarInteiro(linha.amostra)}
            </TabelaCelula>
            <CelulaEtapa rotulo="Até a leitura" etapa={linha.ateLeitura} />
            <CelulaEtapa rotulo="Até a decisão" etapa={linha.ateDecisao} />
            <CelulaEtapa rotulo="Até o agendamento" etapa={linha.ateAgendamento} />
          </TabelaLinha>
        ))}
      </TabelaCorpo>
    </Tabela>
  );
}

export function BlocoTempos({ consulta }: { consulta: string }) {
  const { data, isPending, error } = useQuery({
    queryKey: ['relatorio', 'tempos', consulta],
    queryFn: () => api.get<RelatorioTempos>(`/relatorios/tempos?${consulta}`),
  });

  if (isPending) return <EsqueletoBloco />;
  if (error) return <ErroBloco erro={error} />;

  const { geral } = data;

  return (
    <div className="flex flex-col gap-5">
      {/* A mediana é o número em destaque porque descreve o dia comum: uma
          solicitação esquecida três meses desloca a média e faz a fila parecer
          pior do que é. A distância entre as duas é que mostra a cauda. */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Indicador
          rotulo="Até a leitura pela Regulação"
          valor={formatarDuracao(geral.ateLeitura.mediana)}
          descricao={`Média de ${formatarDuracao(geral.ateLeitura.media)}`}
          icone={MailCheck}
          tom="bg-status-lido-fundo text-status-lido-texto"
        />
        <Indicador
          rotulo="Até a decisão"
          valor={formatarDuracao(geral.ateDecisao.mediana)}
          descricao={`Média de ${formatarDuracao(geral.ateDecisao.media)}`}
          icone={Gavel}
          tom="bg-status-processo-fundo text-status-processo-texto"
        />
        <Indicador
          rotulo="Até o agendamento"
          valor={formatarDuracao(geral.ateAgendamento.mediana)}
          descricao={`Média de ${formatarDuracao(geral.ateAgendamento.media)}`}
          icone={CalendarCheck}
          tom="bg-status-agendado-fundo text-status-agendado-texto"
        />
        <Indicador
          rotulo="Solicitações no período"
          valor={formatarInteiro(geral.amostra)}
          descricao="Base de cálculo das três etapas"
          icone={Sigma}
          tom="bg-marca-suave text-marca"
        />
      </div>

      <p className="text-silenciado-texto text-sm">
        Cada etapa é contada a partir do registro da solicitação. Decisão é o agendamento ou a
        devolução — pendência é etapa, não desfecho, e uma solicitação ainda sem decisão não entra
        na média. O número em destaque é a mediana, o caso típico; a média vem logo abaixo.
      </p>

      <CartaoGrafico
        titulo="Por UBS"
        descricao="Onde o tempo é gasto antes da Regulação assumir o processo."
      >
        <LinhasEtapas
          rotuloColuna="UBS"
          vazio="Sem solicitações no período."
          linhas={data.porUbs.map((linha) => ({ ...linha, chave: linha.ubsId }))}
        />
      </CartaoGrafico>

      <CartaoGrafico
        titulo="Por regulador"
        descricao="Considera apenas solicitações já atribuídas a um regulador."
      >
        <LinhasEtapas
          rotuloColuna="Regulador"
          vazio="Nenhuma solicitação atribuída a regulador no período."
          linhas={data.porRegulador.map((linha) => ({ ...linha, chave: linha.reguladorId }))}
        />
      </CartaoGrafico>

      <CartaoGrafico
        titulo="Origem do agendamento"
        descricao="Quanto o agendamento manual foi necessário — indício indireto de indisponibilidade do eSUS."
      >
        <GraficoRosca
          unidade="agendamentos"
          vazio="Nenhum agendamento confirmado no período."
          fatias={data.agendamentos.map((linha) => ({
            chave: linha.origem,
            rotulo: ROTULOS_ORIGEM[linha.origem],
            valor: linha.total,
          }))}
        />
      </CartaoGrafico>
    </div>
  );
}
